import axios from 'axios';
import { BASE_URL } from '@/globals';

// Cart routes require a JWT; wishlist routes are public but still take userId.
function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Backend identifies a line by book + variant meta (coverType + printType).
// Send the variant only when the user actually picked one; omit it for
// single-variant products so the backend treats it as a variantless line.
function variantPayload(variant) {
  const coverType = variant?.meta?.coverType ?? null;
  const printType = variant?.meta?.printType ?? null;
  if (!coverType && !printType) return undefined;
  return {
    name: variant?.name ?? null,
    meta: { coverType, printType },
  };
}

export const cartApi = {
  get(userId, token) {
    return axios.get(`${BASE_URL}/api/cart`, {
      params: { userId },
      headers: authHeaders(token),
    });
  },
  // Upsert with ABSOLUTE quantity (backend sets, does not increment).
  upsert({ userId, bookId, quantity, variant }, token) {
    const body = { userId, bookId, quantity };
    const v = variantPayload(variant);
    if (v) body.variant = v;
    return axios.post(`${BASE_URL}/api/cart`, body, { headers: authHeaders(token) });
  },
  remove({ userId, bookId, variant }, token) {
    const body = { userId, bookId };
    const v = variantPayload(variant);
    if (v) body.variant = v;
    return axios.post(`${BASE_URL}/api/cart/remove`, body, { headers: authHeaders(token) });
  },
};

export const wishlistApi = {
  get(userId, token) {
    return axios.get(`${BASE_URL}/api/wishlist`, {
      params: { userId },
      headers: authHeaders(token),
    });
  },
  // De-dupes on book + variant server-side (returns 400 if already present).
  add({ userId, bookId, variant }, token) {
    const body = { userId, bookId };
    const v = variantPayload(variant);
    if (v) body.variant = v;
    return axios.post(`${BASE_URL}/api/wishlist`, body, { headers: authHeaders(token) });
  },
  remove({ userId, bookId, variant }, token) {
    const body = { userId, bookId };
    const v = variantPayload(variant);
    if (v) body.variant = v;
    return axios.post(`${BASE_URL}/api/wishlist/remove`, body, { headers: authHeaders(token) });
  },
};

// GET responses only carry { book: { _id, title, price }, variant }, so we
// re-fetch the full product to recover images, author, category and the live
// per-variant price/salePrice/stock the cart & wishlist pages render.
export const productApi = {
  getById(id) {
    return axios.get(`${BASE_URL}/api/products/${id}`);
  },
};
